import React from 'react';
import type { FormData } from '../utils/formUtils';
import type { TaskFieldSettings } from '../hooks/useTaskFieldSettings';

interface TodoFormPrimaryFieldsProps {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  fieldSettings: TaskFieldSettings;
  tagInput: string;
  setTagInput: (value: string) => void;
  tagInputRef: React.RefObject<HTMLInputElement>;
  availableTags: string[];
  showTagSuggestions: boolean;
  setShowTagSuggestions: (value: boolean) => void;
  getFilteredTagSuggestions: () => string[];
  handleTagSuggestionClick: (tag: string) => void;
}

const TodoFormPrimaryFields: React.FC<TodoFormPrimaryFieldsProps> = ({
  formData,
  setFormData,
  fieldSettings,
  tagInput,
  setTagInput,
  tagInputRef,
  availableTags,
  showTagSuggestions,
  setShowTagSuggestions,
  getFilteredTagSuggestions,
  handleTagSuggestionClick,
}) => {
  return (
    <div className="grid grid-cols-2 gap-3">
      {/* Deadline */}
      {fieldSettings.deadline && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Deadline
          </label>
          <div className="relative">
            <input
              type="date"
              value={formData.deadline}
              onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
              onClick={(e) => { e.currentTarget.showPicker?.(); }}
              className="w-full text-sm px-8 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-6 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
            />
            <div className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
              </svg>
            </div>
            {formData.deadline && (
              <button
                type="button"
                onClick={() => setFormData({ ...formData, deadline: '' })}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Clear deadline"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Tags */}
      {fieldSettings.tags && (
        <div className="relative">
          <label className="block text-xs font-medium text-gray-600 mb-1">Tags</label>
          <input
            ref={tagInputRef}
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onFocus={() => setShowTagSuggestions(true)}
            onBlur={() => setTimeout(() => setShowTagSuggestions(false), 200)}
            className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="work, urgent..."
          />
          {showTagSuggestions && availableTags.length > 0 && (() => {
            const filteredSuggestions = getFilteredTagSuggestions();
            return (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
              {filteredSuggestions.length > 0 ? (
                <>
                  <div className="px-3 py-2 text-xs font-medium text-gray-500 bg-gray-50 border-b">Existing Tags</div>
                  {filteredSuggestions.map(tag => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => handleTagSuggestionClick(tag)}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 hover:text-blue-700 text-sm transition-colors flex items-center"
                    >
                      <span className="text-blue-600 mr-2">#</span>{tag}
                    </button>
                  ))}
                </>
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500 italic">No matching tags found</div>
              )}
            </div>
            );
          })()}
          {formData.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {formData.tags.slice(0, 4).map(tag => (
                <span key={tag} className="bg-blue-100 text-blue-800 px-1 py-0.5 rounded text-xs">#{tag}</span>
              ))}
              {formData.tags.length > 4 && (
                <span className="text-xs text-gray-400">+{formData.tags.length - 4}</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TodoFormPrimaryFields;
